import { useCallback, useRef, useState } from "react";
import { MutableRefObject } from "react";
import { RequestFileDto } from "../Schemas/RequestFileDto";
import { Request } from "../Utilities/mappers";
import { UseRequestApi } from "../Api/RequestApi";
import { Snackbar, SnackbarType } from "vig-ui-library";
import { ChangeFileTaxonomyInputSchema } from "../Schemas/ChangeFileTaxonomyInput";
import { getAttachmentFileTaxonomyId } from "../Utilities/fileTaxonomy";

/**
 * Aligns in-memory `file` with `request.categories` by id. Needed when the API omits nested `taxonomy`
 * or returns `{ file: null, taxonomy: null }` on success — we still have the target id from the request payload.
 */
function syncFileTaxonomyFromRequestCategories(
    file: RequestFileDto,
    request: MutableRefObject<Request>,
): void {
    const resolvedId = getAttachmentFileTaxonomyId(file);
    if (resolvedId == null) {
        file.taxonomy = undefined;
        file.taxonomyId = null;
        return;
    }
    const categories = request.current.categories ?? [];
    const taxonomy = categories.find((c) => c.id === resolvedId);
    if (taxonomy) {
        file.taxonomy = taxonomy;
        file.taxonomyId = resolvedId;
    } else if (file.taxonomy?.id !== resolvedId) {
        file.taxonomyId = resolvedId;
    }
}

type UseChangeFileTaxonomyOptions = {
    file: RequestFileDto;
    request: MutableRefObject<Request>;
    requestApi: UseRequestApi;
    openSnackbar: (snackbar: Snackbar) => void;
    rerenderForm: () => void;
    onlySaveRequest: (
        debounced?: boolean,
        shouldReloadRequestData?: boolean,
        successHandler?: () => void,
        fetchFieldsRelation?: boolean,
    ) => void;
};

export const useChangeFileTaxonomy = (options: UseChangeFileTaxonomyOptions) => {
    const { file, request, requestApi, openSnackbar, rerenderForm, onlySaveRequest } = options;
    const busyRef = useRef(false);
    const [isChangingTaxonomy, setIsChangingTaxonomy] = useState(false);

    const changeToTaxonomy = useCallback(
        async (taxonomyId: number) => {
            if (!file.id || !request.current.requestId || busyRef.current) return;
            busyRef.current = true;
            setIsChangingTaxonomy(true);
            try {
                const input = ChangeFileTaxonomyInputSchema.parse({
                    fileId: file.id,
                    requestId: request.current.requestId,
                    taxonomyId,
                    userName: undefined,
                });
                const output = await requestApi.changeFileTaxonomy(input);
                if (output.errorMessage) {
                    openSnackbar({
                        text: output.errorMessage,
                        type: SnackbarType.ERROR,
                    });
                    return;
                }

                if (output.file) {
                    Object.assign(file, output.file);
                }

                if (output.taxonomy != null) {
                    file.taxonomy = output.taxonomy;
                    file.taxonomyId = output.taxonomy.id;
                } else if (!output.file) {
                    // Success with empty body: backend does not return file/taxonomy — use id from request payload.
                    // Clear nested taxonomy so it cannot win over taxonomyId before sync (see getAttachmentFileTaxonomyId).
                    file.taxonomy = undefined;
                    file.taxonomyId = taxonomyId;
                }

                syncFileTaxonomyFromRequestCategories(file, request);
                openSnackbar({
                    text: "Kategória prílohy bola zmenená.",
                    type: SnackbarType.SUCCESS,
                });
                rerenderForm();
                // Re-run saveOrUpdate so attachment validation (e.g. missing front side) is refreshed.
                onlySaveRequest(false);
            } catch {
                openSnackbar({
                    text: "Nepodarilo sa zmeniť kategóriu prílohy.",
                    type: SnackbarType.ERROR,
                });
            } finally {
                busyRef.current = false;
                setIsChangingTaxonomy(false);
            }
        },
        [file, request, requestApi, openSnackbar, rerenderForm, onlySaveRequest],
    );

    return { changeToTaxonomy, isChangingTaxonomy };
};
